import { Typography, ToggleButtonGroup, Dialog } from '@mui/material'
import InCardStack, { ColStack } from 'components/atoms/container/InCardStack'
import RowStack from 'components/atoms/container/RowStack'
import StyledToggleButton from 'components/atoms/input/StyledToggleButton'
import { useTranslation } from 'react-i18next'
import { getLocalUser } from 'utils/localStorage'
import { ReactComponent as TaskSuccessIcon } from 'assets/images/taskSuccess.svg'
import { ReactComponent as SendIcon } from 'assets/images/send.svg'
import { ReactComponent as FeedbackIcon } from 'assets/images/feedback.svg'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import NavigateButtons from 'components/molecules/onboarding/NavigateButtons'
import { IconContainer } from 'components/atoms/Icons'
import LeftPanelTitle from 'components/atoms/container/LeftPanelTitle'
import colors from 'styles/colors'

const celebrate = require('assets/images/celebrateSmall.png')

export const getOnboardingSteps = () => {
  const localUser = getLocalUser()
  const hasCurrentGoal = Boolean(localUser?.userStates?.currentGoalId)
  return [
    {
      number: 1,
      name: 'createYourProfile1',
      greetings: 'finishProfile',
      maxSubSteps: 3
    },
    {
      number: 2,
      name: localUser?.isCohortUser
        ? 'cohortLeadershipJourney'
        : hasCurrentGoal
          ? 'preAssignedGoal'
          : 'personalizeLeadershipJourney',
      maxSubSteps: localUser?.isCohortUser || hasCurrentGoal ? 1 : 2
    },
    {
      number: 3,
      name: 'selfAssessment3',
      showPathName: true,
      stepComplete: {
        title: 'assessments.assessmentComplete',
        description: 'assessments.greatCompletingAssessment',
        icon: <TaskSuccessIcon />
      }
    },
    {
      number: 4,
      name: 'resultAndFeedback',
      showPathName: true,
      stepComplete: {
        title: 'assessmentTimeline.invitesSent',
        description: 'assessmentTimeline.feedbackRequestsSent',
        icon: <SendIcon />,
        skipped: {
          title: 'assessmentTimeline.youSkippedFeedback',
          description: 'assessmentTimeline.canStillAddRaters',
          icon: <FeedbackIcon />
        }
      }
    },
    {
      number: 5,
      name: 'insightSurvey',
      subText: 'userInsightSurvey.getAnUnderstanding',
      backgroundColor: colors.MidnightTeal400,
      isLightText: true
    }
  ]
}

export const TitleGroup = ({ step, goal, ...otherProps }) => {
  const { t } = useTranslation()
  const localUser = getLocalUser()
  const onboardingSteps = getOnboardingSteps()
  return (
    <LeftPanelTitle
      title={t(`onboarding.${onboardingSteps[step].name}.greetings`, {
        firstName: localUser.firstName
      })}
      superText={`${t('common.step')} ${onboardingSteps[step].number} / ${onboardingSteps.length}`}
      subText={
        onboardingSteps[step]?.subText
          ? t(onboardingSteps[step]?.subText)
          : onboardingSteps[step].showPathName && goal
            ? goal.name
            : null
      }
      isLightText={onboardingSteps[step]?.isLightText}
      {...otherProps}
    />
  )
}

export const ToggleOptions = ({
  options,
  value,
  onChange = () => {},
  multiple = false,
  sx = {}
}) => {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive={!multiple}
      onChange={(_event, newValue) => {
        onChange(newValue)
      }}
      sx={{
        gap: 8,
        flexWrap: 'wrap',
        overflowY: 'auto',
        paddingX: 0.5,
        paddingY: 1,
        ...sx
      }}>
      {options.map(v => (
        <StyledToggleButton
          key={`option-${v.value}-${v.value}`}
          value={v.value}
          disabled={v.disabled}>
          {v.label}
        </StyledToggleButton>
      ))}
    </ToggleButtonGroup>
  )
}

const StepItem = ({ step, currentStep, isUpNext = false }) => {
  const { t } = useTranslation()
  const isCurrentStep = currentStep === step.number
  return (
    <RowStack
      padding={'0px 12px 0px 8px'}
      justifyContent='space-between'
      sx={{
        ...(isCurrentStep && {
          padding: '4px 8px',
          backgroundColor: colors.MutedTeal100,
          borderRadius: 2
        })
      }}>
      <Typography
        variant='eyebrow'
        sx={{
          ...(isCurrentStep && { fontSize: 15, fontWeight: 600 })
        }}>
        {step.number}. {t(`onboarding.${step?.name}.title`)}
        {isUpNext && step.number === 4 ? (
          <Typography
            component={'span'}
            variant='body1Small'
            color={'textSecondary'}
            textTransform={'capitalize'}
            fontWeight={400}>
            {` (${t('company.optional')})`}
          </Typography>
        ) : null}
      </Typography>

      {isUpNext ? (
        <CheckCircleOutlineOutlinedIcon
          sx={{ color: colors.Grey200, fontSize: 24 }}
        />
      ) : (
        <CheckCircleIcon
          sx={{
            color: isCurrentStep ? colors.Teal500 : colors.MutedTeal400,
            fontSize: isCurrentStep ? 32 : 24
          }}
        />
      )}
    </RowStack>
  )
}

const StepsList = ({ steps, currentStep = 0, isUpNext = false }) => {
  const { t } = useTranslation()
  return (
    <ColStack
      spacing={4}
      sx={{
        padding: isUpNext ? '12px 6px' : '12px 6px 6px 6px',
        border: `1px solid ${colors.MutedTeal300}`,
        borderRadius: 3
      }}>
      {isUpNext && (
        <Typography variant='h6' color='textSecondary' paddingX={4}>
          {t(`onboarding.${steps.length === 1 ? 'lastStep' : 'upNext'}`)}
        </Typography>
      )}
      {steps.map((step, index) => (
        <StepItem
          key={index}
          step={step}
          currentStep={currentStep}
          isUpNext={isUpNext}
        />
      ))}
    </ColStack>
  )
}

export const OnboardingStepCompletePopup = ({
  open,
  handleContinue,
  currentOnboardingStep = 0, // its the index of the onboardingSteps
  skippedFeedback,
  handleBack
}) => {
  const { t } = useTranslation()
  const onboardingSteps = getOnboardingSteps()
  const isOnboardingComplete =
    currentOnboardingStep === onboardingSteps.length - 1
  const currentStep = onboardingSteps[currentOnboardingStep]
  const stepComplete = skippedFeedback
    ? currentStep?.stepComplete?.skipped
    : currentStep?.stepComplete
  const doneSteps = onboardingSteps.filter(
    step => step.number <= currentStep?.number
  )
  const upcomingSteps = onboardingSteps.filter(
    step => step.number > currentStep?.number
  )
  return (
    <Dialog open={open}>
      <InCardStack
        width={480}
        spacing={12}
        padding={20}
        sx={{
          ...(isOnboardingComplete && {
            background: `url(${celebrate}) no-repeat`,
            backgroundSize: 'contain',
            backgroundPositionY: -150,
            paddingTop: 50
          })
        }}>
        <IconContainer
          icon={
            !stepComplete?.icon || isOnboardingComplete ? (
              <TaskSuccessIcon />
            ) : (
              stepComplete?.icon
            )
          }
          size={60}
        />
        <InCardStack>
          <InCardStack spacing={4}>
            {!isOnboardingComplete && !skippedFeedback ? (
              <Typography
                variant='eyebrow'
                fontSize={14}
                fontWeight={700}
                color='textSecondary'>
                {t('assessments.stepNOfM', {
                  n: currentStep?.number,
                  m: onboardingSteps.length
                })}
              </Typography>
            ) : null}

            <Typography variant='h2'>
              {t(
                isOnboardingComplete && !skippedFeedback
                  ? 'onboarding.onboardingComplete'
                  : stepComplete?.title
              )}
            </Typography>
          </InCardStack>

          <Typography textAlign={'center'}>
            {t(
              isOnboardingComplete && !skippedFeedback
                ? 'onboarding.youAreAllSet'
                : stepComplete?.description
            )}
          </Typography>

          <InCardStack spacing={4} width='100%'>
            <StepsList steps={doneSteps} currentStep={currentStep?.number} />
            {upcomingSteps.length > 0 && (
              <StepsList steps={upcomingSteps} isUpNext={true} />
            )}
          </InCardStack>
        </InCardStack>

        <NavigateButtons
          showBack={skippedFeedback}
          onBack={handleBack}
          onNext={handleContinue}
          nextLabel={
            isOnboardingComplete ? t('addRaters.getStarted') : undefined
          }
        />
      </InCardStack>
    </Dialog>
  )
}
